import {
  SingleDonationFormValuesAuthenticatedUser,
  SingleDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/SingleDonationForm/SingleDonationForm';
import {
  transformCreateDonationViolationsToDonationFormErrors,
  transformSingleDonationFormValuesToCreateDonationRequest,
} from '@/app/components/DonationForm/utils';
import { PaymentMethod } from '@/app/enums';
import useAnalytics from '@/app/hooks/useAnalytics';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { useToast } from '@/app/hooks/useToast';
import { useAbGroup } from '@/app/providers/ConfigProvider';
import { useProjectData } from '@/app/providers/ProjectProvider';
import { isResponseError } from '@/app/utils/apiCommonUtils';
import { FormikHelpers } from 'formik';
import { useCallback } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'react-i18next';
import { useRedirectToDonationDetail } from '@/app/hooks/useRedirecToDonationDetail';
import { useRouter } from 'next/navigation';
import { usePostCreateDonation } from '@/src/api/client';
import { CreateDonationResponse } from '@/app/api';

type HandleSubmitParams = {
  onSuccess?: (donationId: string) => void;
  onFailure?: () => void;
};

export const useHandleSubmit = ({ onFailure, onSuccess }: HandleSubmitParams) => {
  const { project, subProject } = useProjectData();
  const abGroup = useAbGroup();
  const router = useRouter();
  const { currentUser } = useCurrentUser();
  const { t } = useTranslation();
  const {
    donation: { pushDonationCompletedEvent },
  } = useAnalytics();
  const { showErrorToast, showGenericErrorToast } = useToast();
  const isTipDisabled = project.settings.hasTipsDisabled;
  const redirectToDonationDetail = useRedirectToDonationDetail();
  const { mutateAsync: createDonationAsync } = usePostCreateDonation();

  const handleSubmit = useCallback(
    async (
      values: SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser,
      helpers: FormikHelpers<SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser>,
    ) => {
      try {
        const transformedValues = transformSingleDonationFormValuesToCreateDonationRequest(
          project.id,
          values,
          currentUser?.id,
          subProject?.id,
          abGroup,
        );

        const response = (await createDonationAsync({
          data: transformedValues,
        })) as CreateDonationResponse;

        onSuccess?.(response.id);

        if (values.paymentMethod !== PaymentMethod.Card) {
          pushDonationCompletedEvent({
            donationId: response.id,
            requestDto: transformedValues,
            project,
            paymentMethod: values.paymentMethod,
          });
        }

        redirectToDonationDetail({ id: response.publicId, paymentMethod: values.paymentMethod });
      } catch (error: unknown) {
        const isValidationError = isResponseError(error) && error.response.status === 422;

        if (!isValidationError) {
          console.log(error);
          console.log(values);
          Sentry.captureException(error);
        }

        onFailure?.();

        if (!isResponseError(error)) {
          return showGenericErrorToast();
        }

        switch (error.response.status) {
          case 422:
            const responseJson = await error.response.json();
            const transformedErrors = transformCreateDonationViolationsToDonationFormErrors(responseJson.violations);
            helpers.setErrors(transformedErrors);

            return showErrorToast(t('messages.422'));

          default:
            showGenericErrorToast();
        }
      }
      helpers.setSubmitting(false);
    },
    [
      onSuccess,
      onFailure,
      router,
      showErrorToast,
      showGenericErrorToast,
      project.id,
      isTipDisabled,
      currentUser,
      t,
      subProject,
    ],
  );

  return handleSubmit;
};
